import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";

/**
 * The working-tree half of a code review, for someone reading changes on a phone.
 *
 * A review screen shows two different things and a person tells them apart at a
 * glance: what this branch has committed since it left its base, and what is
 * still uncommitted in the worktree. Forge already serves the per-turn diffs
 * through `orchestration.getTurnDiff`, so the mobile Review screen was half
 * populated and reported itself unavailable, because the git half had no route.
 *
 * The shapes mirror `@t3tools/contracts` exactly, deliberately. That package is
 * the vendored client and does not import this one, so the only thing keeping
 * them in agreement is that they were written to match and a parity test that
 * fails when they stop matching.
 */

/**
 * Two kinds, because they answer different questions and merging them would
 * make "has this been committed yet" unanswerable from the screen.
 */
export const ReviewDiffPreviewSourceKind = Schema.Literals(["working-tree", "branch-range"]);
export type ReviewDiffPreviewSourceKind = typeof ReviewDiffPreviewSourceKind.Type;

/**
 * `diffHash` is what lets the client tell "unchanged since I last looked" from
 * "changed back to something that looks similar", without diffing the diff.
 *
 * `truncated` is not decoration. A review that silently stops after some number
 * of bytes is a review of the wrong thing, so a person has to be told.
 */
export const ReviewDiffPreviewSource = Schema.Struct({
  id: TrimmedNonEmptyString,
  kind: ReviewDiffPreviewSourceKind,
  title: TrimmedNonEmptyString,
  baseRef: Schema.NullOr(TrimmedNonEmptyString),
  headRef: Schema.NullOr(TrimmedNonEmptyString),
  diff: Schema.String,
  diffHash: TrimmedNonEmptyString,
  truncated: Schema.Boolean,
});
export type ReviewDiffPreviewSource = typeof ReviewDiffPreviewSource.Type;

/**
 * `baseRef` is optional and the client does not send it: mobile passes only
 * `cwd` and lets the server resolve the base, because the phone does not know
 * what this branch forked from and the server already does.
 */
export const ReviewDiffPreviewInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  baseRef: Schema.optional(TrimmedNonEmptyString),
  ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
});
export type ReviewDiffPreviewInput = typeof ReviewDiffPreviewInput.Type;

export const ReviewDiffPreviewResult = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  generatedAt: Schema.DateTimeUtc,
  sources: Schema.Array(ReviewDiffPreviewSource),
});
export type ReviewDiffPreviewResult = typeof ReviewDiffPreviewResult.Type;

export const ReviewDiffPreviewFailure = Schema.Literals([
  "not_a_repository",
  "base_ref_unresolved",
  "diff_failed",
]);
export type ReviewDiffPreviewFailure = typeof ReviewDiffPreviewFailure.Type;

function decodedReviewErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

export class ReviewDiffPreviewError extends Schema.TaggedErrorClass<ReviewDiffPreviewError>()(
  "ReviewDiffPreviewError",
  {
    cwd: Schema.optional(TrimmedNonEmptyString),
    baseRef: Schema.optional(TrimmedNonEmptyString),
    failure: Schema.optional(ReviewDiffPreviewFailure),
    detail: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // Structured fields stay optional on the wire so a newer peer can decode a
  // legacy message-only failure. New code goes through this constructor.
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly cwd: string;
    readonly baseRef?: string;
    readonly failure: ReviewDiffPreviewFailure;
    readonly detail?: string;
    readonly cause?: unknown;
  }) {
    super({
      ...props,
      message:
        decodedReviewErrorMessage(props) ?? `Could not build a diff preview for '${props.cwd}'.`,
    } as any);
  }
}
